import { Crosshair, Shield } from "lucide-react";
import { TYPE_BADGES } from "./feedConstants";
import { useCompanyContext } from "../../../context";

/* ─── Content type badge component ─── */
export const TypeBadge = ({ type, t }) => {
  const badge = TYPE_BADGES[type];
  if (!badge) return null;
  const Icon = badge.icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap"
      style={{
        // ~10% tint of the badge color — VN badge pattern: bg-color/10
        background: `${badge.color}1a`,
        color: badge.color,
        // ~30% opacity border — visible but not heavy
        border: `1px solid ${badge.color}4d`,
      }}
    >
      <Icon size={10} style={{ flexShrink: 0 }} />
      {badge.label}
    </span>
  );
};

/* ─── Target badge (shows target value with crosshair icon) ─── */
const TARGET_COLOR = "#22d3ee";
export const TargetBadge = ({ target, t }) => {
  if (!target) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap"
      style={{
        background: `${TARGET_COLOR}1a`,
        color: TARGET_COLOR,
        border: `1px solid ${TARGET_COLOR}4d`,
        maxWidth: 160,
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      <Crosshair size={10} style={{ flexShrink: 0 }} />
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {target}
      </span>
    </span>
  );
};

/* ─── APT name badge (shows apt_name with shield icon) ─── */
const APT_COLOR = "#f472b6";
export const AptBadge = ({ aptName, t }) => {
  if (!aptName) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap"
      style={{
        background: `${APT_COLOR}1a`,
        color: APT_COLOR,
        border: `1px solid ${APT_COLOR}4d`,
        maxWidth: 160,
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      <Shield size={10} style={{ flexShrink: 0 }} />
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {aptName}
      </span>
    </span>
  );
};

/* ─── Card info overlay (always visible at top of card) ─── */
export const CardPreview = ({ article, t }) => {
  const { currentCompany } = useCompanyContext();
  if (!article) return null;
  const title = article.title || article.url || "Untitled";
  const source =
    article.source ||
    (article.url
      ? (() => {
          try {
            return new URL(article.url).hostname.replace("www.", "");
          } catch {
            return "";
          }
        })()
      : "");
  const snippet = article.content
    ? article.content.length > 180
      ? article.content.slice(0, 180).trim() + "…"
      : article.content
    : "";
  const type = article.target_type || article.type;
  const aptName = article.apt_name || null;
  return (
    <div
      className="px-3 py-2.5"
      style={{
        background: t.barBg,
        borderBottom: `1px solid ${t.divider}`,
        pointerEvents: "none",
      }}
    >
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        {currentCompany?.case && (
          <TargetBadge target={currentCompany.case} t={t} />
        )}
        {type && <TypeBadge type={type} t={t} />}
        {aptName && <AptBadge aptName={aptName} t={t} />}
        {source && (
          <span className="text-[10px] truncate" style={{ color: t.textMuted }}>
            {source}
          </span>
        )}
      </div>
      <p
        className="text-xs font-medium leading-snug line-clamp-2 mb-1"
        style={{ color: t.textPrimary }}
      >
        {title}
      </p>
      {snippet && (
        <p
          className="text-[11px] leading-relaxed line-clamp-3"
          style={{ color: t.textSecondary }}
        >
          {snippet}
        </p>
      )}
    </div>
  );
};
